/* eslint-disable react-hooks/exhaustive-deps */
import { addToCartFetcher, updateCartFetcher, useCartSWRKey } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useSettings } from '@/data/Settings';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { ORDER_CONFIGS } from '@/data/constants/order';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { OrderItem } from '@/data/types/Order';
import { dFix } from '@/data/utils/floatingPoint';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { usableShippingInfoMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/usableShippingInfoMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { useCallback, useMemo } from 'react';
import { mutate } from 'swr';
import { ProductDisplayPrice, ProductType, ResponseProductAttribute } from '../types/Product';
import { CatSEO } from '../types/Category';
import { useProductsWithParent } from './Products';

export type ColumnWithKey = {
	key: string;
	numeric?: boolean;
	[extra: string]: any;
};

export const useOrderItemList = (orderItems: OrderItem[], orderId: string) => {
	const { settings } = useSettings();
	const { storeId } = settings;
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const currentCartSWRKey = useCartSWRKey(); // in current language

	const updateOrderItemSize = useCallback(
		(orderItemId: string) => async (productId: string | null) => {
			if (productId !== null && orderItemId) {
				const dataBase = {
					orderId,
					x_calculateOrder: ORDER_CONFIGS.calculateOrder,
					x_calculationUsage: ORDER_CONFIGS.calculationUsage,
					x_inventoryValidation: ORDER_CONFIGS.inventoryValidation.toString(),
				};

				const dataToAdd = {
					...dataBase,
					orderItem: [
						{
							quantity: '1',
							productId,
						},
					],
				};

				const dataToRemove = {
					...dataBase,
					orderItem: [
						{
							quantity: '0',
							orderItemId,
						},
					],
				};

				try {
					await updateCartFetcher(true)(storeId ?? '', {}, dataToRemove, params);
					await addToCartFetcher(true)(storeId ?? '', {}, dataToAdd, params);
					await mutate(cartMutatorKeyMatcher(EMPTY_STRING));
					await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined);
					await mutate(usableShippingInfoMutatorKeyMatcher(EMPTY_STRING), undefined);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[orderId, storeId, params, currentCartSWRKey, notifyError]
	);

	const updateOrderItem = useCallback(
		(orderItemId: string) => async (quantity: number | null) => {
			if (quantity !== null && orderItemId) {
				const orderItem = {
					quantity: quantity.toString(),
					orderItemId,
				};

				const data = {
					orderId,
					x_calculateOrder: ORDER_CONFIGS.calculateOrder,
					orderItem: [orderItem],
					x_calculationUsage: ORDER_CONFIGS.calculationUsage,
					x_inventoryValidation: ORDER_CONFIGS.inventoryValidation.toString(),
				};
				try {
					await updateCartFetcher(true)(storeId ?? '', {}, data, params);
					await mutate(cartMutatorKeyMatcher(EMPTY_STRING)); // at current page
					await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined); // all cart except current cart, e.g different locale
					await mutate(usableShippingInfoMutatorKeyMatcher(EMPTY_STRING), undefined);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[orderId, storeId, params, currentCartSWRKey, notifyError]
	);

	const data = useMemo(
		() =>
			orderItems
				? orderItems.map((orderItem) => {
						const {
							partNumber,
							quantity,
							orderItemId,
							orderItemPrice,
							unitPrice,
							currency,
							contractId,
							adjustment,
						} = orderItem || {};

						return {
							itemDetails: {
								partNumber,
								orderItemId,
								contractId,
								currency,
								unitPrice,
								key: 'partNumber',
							},
							quantity: {
								quantity: dFix(quantity, 0),
								onChange: updateOrderItem(orderItemId),
								key: 'quantity',
								numeric: true,
							},
							price: {
								orderItemPrice,
								currency,
								key: 'orderItemPrice',
								numeric: true,
							},
							sizes: {
								onChange: updateOrderItemSize(orderItemId),
								key: 'sizes',
							},
							totalAdjustment: {
								totalAdjustment: orderItem.totalAdjustment.value,
								currency,
								key: 'totalAdjustment',
								numeric: true,
							},
							adjustment,
							orderItem,
						};
				  })
				: [],
		[orderItems]
	);

	return { data };
};

const EMPTY_PRICE = {} as ProductDisplayPrice;
export const EMPTY_PROD = {} as ProductType;
const EMPTY_ATTRS: ResponseProductAttribute[] = [];

export type OrderItemListRows = {
	details: {
		partNumber: string;
		name: string;
		thumbnail: string;
		prices: ProductDisplayPrice;
		attributes: ResponseProductAttribute[];
		manufacturer: string;
		seller: string;
		sellerId: string;
		contractId: string;
		seo?: CatSEO;
	};
	parentProduct?: ProductType;
	product: ProductType;
	orderItemListRow: ReturnType<typeof useOrderItemList>['data'][0];
}[];

export const EMPTY_ORDER_ITEM_LIST_ROWS = [
	{
		details: {
			partNumber: '',
			name: '',
			thumbnail: '',
			prices: EMPTY_PRICE,
			attributes: EMPTY_ATTRS,
			manufacturer: '',
			seller: '',
			sellerId: '',
			contractId: '',
		},
		product: EMPTY_PROD,
		orderItemListRow: {} as ReturnType<typeof useOrderItemList>['data'][0],
	},
] as OrderItemListRows;

export const useOrderItemListRows = (
	orderItemList: ReturnType<typeof useOrderItemList>['data']
) => {
	const partNumber = useMemo(() => {
		if (!orderItemList?.length) {
			return [];
		}

		return orderItemList.map((item) => item.itemDetails.partNumber);
	}, [orderItemList]);

	const { products, loading } = useProductsWithParent({
		partNumber,
	});

	const data = useMemo(() => {
		if (!products.length || !orderItemList.length) {
			return [];
		}

		const response = orderItemList.map((item) => {
			const { partNumber, contractId } = item.itemDetails;

			const product = products.find((product) => product.partNumber === partNumber);

			const {
				attributes = EMPTY_ATTRS,
				name = '',
				productPrice = EMPTY_PRICE,
				manufacturer = '',
				thumbnail = '',
				sellerId,
				seller,
			} = product || EMPTY_PROD;

			return {
				details: {
					partNumber,
					name,
					thumbnail,
					prices: productPrice,
					attributes,
					manufacturer,
					seller,
					sellerId,
					contractId,
					seo: product?.seo,
				},
				product: product || EMPTY_PROD,
				parentProduct: product?.parentProduct || EMPTY_PROD,
				orderItemListRow: item,
			};
		});

		return response;
	}, [products, orderItemList]);

	return { data, loading };
};
